import type { WorksheetSubmissionGroupEntity } from '@packages/domain/techline/worksheet-submissions';

import { noop } from '@packages/core/utils';
import { Button, Typography } from '@packages/ui/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import type { WorksheetGroupWithTitles } from '../../submitted-case-subview.types';

import {
    type WorksheetGroup,
    WorksheetGroupAccordion,
    type WorksheetOpenMode,
} from '../../../../worksheet-group-accordion';
import { CollapsibleSection } from '../collapsible-section';
import { AddWorksheetModal } from './add-worksheet-modal';

const SECTION_SUBTITLE = 'Complete extra worksheets if requested or needed for diagnosis.';

/**
 * Async no-op for callback props that require a Promise return type.
 *
 * @returns a resolved promise
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional no-op for async callback props
async function asyncNoop(): Promise<void> {}

/** Inline style for the worksheet loading spinner container. */
const worksheetSpinnerStyle = { display: 'flex', justifyContent: 'center', padding: '40px 0' };

/** Inline style for the spacer div below the worksheet section, providing scroll breathing room. */
const worksheetSpacerStyle = { height: 200 };

/**
 * Props for the {@link WorksheetsSection} component.
 */
export type WorksheetsSectionProps = {
    /** Whether the user can add worksheets to the case, used to conditionally render the "Add new worksheet" button. */
    allowAdditionalWorksheets: boolean;

    /** Whether the user can edit the submitted case. Users with the DSQM role cannot edit submitted cases. */
    canEditSubmittedCase: boolean;

    /** Whether all worksheets added to the case have been submitted. */
    hasAllWorksheetsSubmitted: boolean;

    /** Whether the case is closed; disables the "Add new worksheet" button when true. */
    isCaseClosed: boolean;

    /** Whether worksheet submission groups are currently being fetched. */
    isLoadingWorksheets: boolean;

    /** Whether the current user is a pilot user, used when fetching worksheet titles. */
    isPilotUser: boolean;

    /** Callback invoked to delete a draft worksheet submission. Returns true on success. */
    onDeleteDraftWorksheetSubmission: (submissionId: string) => Promise<boolean>;

    /** Callback to report an error message to the parent view. */
    onError: (message: string) => void;

    /** Callback to fetch available worksheet titles for the add-worksheet modal. */
    onFetchWorksheetTitles: (params: {
        /** Whether the current user is a pilot user. */
        isPilotUser: boolean;
    }) => Promise<WorksheetGroupWithTitles[] | undefined>;

    /** Callback to navigate to the worksheet editor for a given worksheet. */
    onOpenWorksheet: (worksheetId: string, submissionId: string | undefined, mode: WorksheetOpenMode) => void;

    /** Callback to report a success message to the parent view. */
    onSuccess: (message: string) => void;

    /** Worksheet submission groups for the case; null when not yet loaded. */
    worksheetGroupData: WorksheetSubmissionGroupEntity[] | null;

    /** Available worksheet definitions for the add-worksheet modal dropdown. */
    worksheetTitleData: WorksheetGroupWithTitles[] | undefined;

    /** Map of worksheet ID to validation result (true = valid, false = missing required info). */
    worksheetValidationResults: Record<string, boolean>;
};

/**
 * Encapsulates the "Worksheets" section of the submitted case view.
 * Renders a collapsible accordion of worksheet groups, a loading spinner,
 * an empty-state message, and the "Add new worksheet" modal with its
 * cascading group/worksheet dropdowns.
 *
 * @param props - {@link WorksheetsSectionProps}
 * @returns the worksheets section JSX
 */
export function WorksheetsSection({
    hasAllWorksheetsSubmitted,
    canEditSubmittedCase,
    allowAdditionalWorksheets,
    isCaseClosed,
    isLoadingWorksheets,
    isPilotUser,
    onDeleteDraftWorksheetSubmission,
    onError,
    onFetchWorksheetTitles,
    onOpenWorksheet,
    onSuccess,
    worksheetGroupData,
    worksheetTitleData,
    worksheetValidationResults,
}: WorksheetsSectionProps) {
    // State
    const [selectedGroupId, setSelectedGroupId] = useState<string>('');
    const [showWorksheetModal, setShowWorksheetModal] = useState(false);
    const [worksheetSectionExpanded, setWorksheetSectionExpanded] = useState(true);

    const hasExpandedFromParamRef = useRef(false);
    const lastWorksheetRef = useRef<HTMLDivElement>(null);
    const worksheetContainerRef = useRef<HTMLDivElement>(null);

    const sectionSubTitle = allowAdditionalWorksheets ? SECTION_SUBTITLE : '';

    // Hooks
    const [searchParams, setSearchParams] = useSearchParams();

    // Derived state
    const showRightActionButton = allowAdditionalWorksheets && canEditSubmittedCase;

    /**
     * Dropdown options for the group selector in the add-worksheet modal.
     * Includes a placeholder option followed by all available worksheet groups.
     */
    const worksheetGroupOptions = useMemo(() => {
        return [
            { value: '', title: 'Select a group' },
            ...(worksheetTitleData?.map((group) => ({
                value: group.worksheetCaseGroupId,
                title: group.groupName,
            })) || []),
        ];
    }, [worksheetTitleData]);

    /**
     * Worksheet options for the selected group in the add-worksheet modal. Lists every worksheet in the selected
     * group; duplicates are allowed because users can create multiple independent instances of the same worksheet
     * (TECHSHARE-6411).
     */
    const worksheetModalOptions = useMemo(() => {
        if (!worksheetTitleData || !selectedGroupId) {
            return [{ value: '', title: 'Select a worksheet' }];
        }

        const availableWorksheets =
            worksheetTitleData.find((group) => group.worksheetCaseGroupId === selectedGroupId)?.worksheetTitles || [];

        return [
            { value: '', title: 'Select a worksheet' },
            ...availableWorksheets.map((worksheet) => ({
                value: worksheet.worksheetId,
                title: worksheet.displayName,
            })),
        ];
    }, [selectedGroupId, worksheetTitleData]);

    /**
     * Pre-computed worksheet groups and their selected IDs for the worksheet accordion list.
     * Sorts submissions by status (SUBMITTED -> UPLOADED -> DRAFT) and maps to the shape
     * expected by WorksheetGroupAccordion.
     */
    const preparedWorksheetGroupData = useMemo(() => {
        if (!worksheetGroupData?.length) {
            return [];
        }

        const statusOrder: Record<string, number> = { SUBMITTED: 0, UPLOADED: 1, DRAFT: 2 };

        return worksheetGroupData.map((submissionGroup) => {
            // Count submissions per worksheetId in this group so rows can flag multi-instance worksheets
            const submissionCountByWorksheetId = new Map<string, number>();
            for (const title of submissionGroup.worksheetSubmissionTitles ?? []) {
                submissionCountByWorksheetId.set(
                    title.worksheetId,
                    (submissionCountByWorksheetId.get(title.worksheetId) ?? 0) + 1
                );
            }

            // Sort worksheets by status priority
            const sortedWorksheets = submissionGroup.worksheetSubmissionTitles?.sort((a, b) => {
                const aOrder = statusOrder[a.status] ?? 3;
                const bOrder = statusOrder[b.status] ?? 3;
                return aOrder - bOrder;
            });

            // Map to the shape expected by WorksheetGroupAccordion
            const group: WorksheetGroup = {
                worksheetCaseGroupId: submissionGroup.worksheetCaseGroupId,
                groupName: submissionGroup.groupName,
                groupDescription: submissionGroup.groupDescription,
                worksheetTitles:
                    sortedWorksheets?.map((worksheet) => ({
                        worksheetId: worksheet.worksheetId,
                        displayName: worksheet.worksheetTitle,
                        worksheetDescription: worksheet.worksheetDescription || '',
                        hasMultipleSubmissions: (submissionCountByWorksheetId.get(worksheet.worksheetId) ?? 0) >= 2,
                        lastUpdatedTimestamp: worksheet.lastUpdatedTimestamp,
                        submissionId: worksheet.submissionId,
                        status: worksheet.status,
                    })) || [],
                worksheetsLoaded: true,
            };

            // Collect the selected worksheet IDs for this group
            const selectedWorksheetIds = submissionGroup.worksheetSubmissionTitles?.map((w) => w.worksheetId) || [];

            return { group, selectedWorksheetIds };
        });
    }, [worksheetGroupData]);

    // Handlers

    /**
     * Handle adding a worksheet via the modal. Opens the worksheet editor
     * and resets the selected group.
     *
     * @param _groupId - the selected group ID (unused)
     * @param worksheetId - the worksheet definition ID to open
     */
    const handleAddWorksheetModalConfirm = useCallback(
        (_groupId: string, worksheetId: string) => {
            onOpenWorksheet(worksheetId, undefined, 'add');
            setSelectedGroupId('');
        },
        [onOpenWorksheet]
    );

    /**
     * Handle hiding the add-worksheet modal. Resets the selected group.
     */
    const handleAddWorksheetModalHide = useCallback(() => {
        setShowWorksheetModal(false);
        setSelectedGroupId('');
    }, []);

    /**
     * Handle deleting a draft worksheet submission. Shows success/error feedback via parent callbacks.
     *
     * @param _worksheetId - the worksheet definition ID (unused; kept for callback signature)
     * @param submissionId - the ID of the submission to delete
     */
    const handleDeleteWorksheetButtonClick = useCallback(
        async (_worksheetId: string, submissionId: string) => {
            try {
                const success = await onDeleteDraftWorksheetSubmission(submissionId);
                if (success) {
                    onSuccess('Worksheet deleted successfully');
                    setTimeout(() => onSuccess(''), 10000);
                }
            } catch {
                onError('Failed to delete worksheet');
                setTimeout(() => onError(''), 10000);
            }
        },
        [onDeleteDraftWorksheetSubmission, onError, onSuccess]
    );

    /**
     * Handle downloading a worksheet. Currently a stub that shows a "coming soon" message.
     *
     * @param _worksheetId - the worksheet definition ID
     * @param _submissionId - the submission ID
     */
    const handleDownloadWorksheetButtonClick = useCallback(
        async (_worksheetId: string, _submissionId: string) => {
            try {
                onSuccess('Worksheet download functionality coming soon');
                setTimeout(() => onSuccess(''), 3000);
            } catch {
                onError('Failed to download worksheet');
                setTimeout(() => onError(''), 3000);
            }
        },
        [onError, onSuccess]
    );

    /**
     * Handle navigating to edit a worksheet submission.
     *
     * @param worksheetId - the worksheet definition ID
     * @param submissionId - the submission ID
     */
    const handleEditWorksheetButtonClick = useCallback(
        async (worksheetId: string, submissionId: string) => {
            onOpenWorksheet(worksheetId, submissionId, 'edit');
        },
        [onOpenWorksheet]
    );

    /**
     * Handle the "Add new worksheet" button click. Opens the add-worksheet modal.
     */
    const handleShowWorksheetModalButtonClick = useCallback(() => {
        setShowWorksheetModal(true);
    }, []);

    /**
     * Handle viewing a worksheet submission (same as edit; opens the worksheet editor).
     *
     * @param worksheetId - the worksheet definition ID
     * @param submissionId - the submission ID
     */
    const handleViewWorksheetButtonClick = useCallback(
        async (worksheetId: string, submissionId: string) => {
            onOpenWorksheet(worksheetId, submissionId, 'view');
        },
        [onOpenWorksheet]
    );

    /**
     * Handle changing the selected group in the add-worksheet modal.
     *
     * @param groupId - the selected group ID
     */
    const handleWorksheetGroupChange = useCallback((groupId: string) => {
        setSelectedGroupId(groupId);
    }, []);

    // Effects

    /** Expand the worksheet section when returning from the worksheet editor via URL param. */
    useEffect(() => {
        if (hasExpandedFromParamRef.current) {
            return;
        }

        const shouldExpand = searchParams.get('expandWorksheets') === 'true';
        if (shouldExpand) {
            hasExpandedFromParamRef.current = true;
            setWorksheetSectionExpanded(true);

            // Clear the param from the URL without triggering a navigation
            searchParams.delete('expandWorksheets');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    /** Scroll to the last worksheet when the section is expanded and loading completes. */
    useEffect(() => {
        if (worksheetSectionExpanded && lastWorksheetRef.current && hasExpandedFromParamRef.current) {
            setTimeout(() => {
                lastWorksheetRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest',
                });
            }, 1200);
        }
    }, [isLoadingWorksheets, worksheetSectionExpanded, worksheetGroupData?.length]);

    /** Fetch worksheet titles when the add-worksheet modal opens. */
    useEffect(() => {
        if (showWorksheetModal && !worksheetTitleData) {
            onFetchWorksheetTitles({ isPilotUser });
        }
    }, [showWorksheetModal, worksheetTitleData, isPilotUser, onFetchWorksheetTitles]);

    return (
        <>
            <CollapsibleSection
                expanded={worksheetSectionExpanded}
                iconName={!hasAllWorksheetsSubmitted ? 'warning' : undefined}
                onExpandedChange={setWorksheetSectionExpanded}
                rightAction={
                    <Button
                        disabled={isCaseClosed}
                        onPress={handleShowWorksheetModalButtonClick}
                        // eslint-disable-next-line no-restricted-syntax -- single-property style (convention exception)
                        style={{ marginRight: 4 }}
                        variant="ghost-blue"
                    >
                        Add new worksheet
                    </Button>
                }
                showDividerTop
                showRightAction={showRightActionButton}
                subtitle={sectionSubTitle}
                title="Worksheets"
            >
                {isLoadingWorksheets ? (
                    <div style={worksheetSpinnerStyle}>
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading worksheets...</span>
                        </Spinner>
                    </div>
                ) : preparedWorksheetGroupData.length > 0 ? (
                    <div ref={worksheetContainerRef}>
                        {preparedWorksheetGroupData.map(({ group, selectedWorksheetIds }, index) => {
                            const isLastWorksheet = index === preparedWorksheetGroupData.length - 1;

                            return (
                                <div key={group.worksheetCaseGroupId} ref={isLastWorksheet ? lastWorksheetRef : null}>
                                    <WorksheetGroupAccordion
                                        group={group}
                                        isCaseClosed={isCaseClosed}
                                        isSelected={true}
                                        onCompleteWorksheet={asyncNoop}
                                        onDeleteWorksheet={handleDeleteWorksheetButtonClick}
                                        onDownloadWorksheet={handleDownloadWorksheetButtonClick}
                                        onEditWorksheet={handleEditWorksheetButtonClick}
                                        onGroupSelect={noop}
                                        onViewWorksheet={handleViewWorksheetButtonClick}
                                        onWorksheetSelect={noop}
                                        selectedWorksheets={selectedWorksheetIds}
                                        showInstructionText={false}
                                        worksheetValidationResults={worksheetValidationResults}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <Typography color="textDarkSecondary" variant="default">
                        No worksheets have been added to this case.
                    </Typography>
                )}
            </CollapsibleSection>

            <div style={worksheetSpacerStyle} />

            <AddWorksheetModal
                groupOptions={worksheetGroupOptions}
                isLoading={!worksheetTitleData && showWorksheetModal}
                onConfirm={handleAddWorksheetModalConfirm}
                onGroupChange={handleWorksheetGroupChange}
                onHide={handleAddWorksheetModalHide}
                show={showWorksheetModal}
                worksheetOptions={worksheetModalOptions}
            />
        </>
    );
}
